import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { apiDomain } from "configs/configs";
import { logout } from "helpers/helpers";
import {
  Select,
  Flex,
  Input,
  Form,
  Row,
  Col,
  Typography,
  Button,
  Upload,
  UploadFile,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import styles from "pages/admin/masters/masters.module.scss";
import { UploadChangeParam } from "antd/es/upload/interface";
import MobileInnerWrapper from "layouts/mobileInnerWrapper/mobileInnerWrapper";
import Table, { ColumnsType } from "components/table/table";
import { useParams, useNavigate } from "react-router-dom";
import { create } from "zustand";

const { Text } = Typography;
const { TextArea } = Input;

type ComponentProps = {};

type KnowledgeBase = {
  id: number;
  name: string;
  parent_id: number;
  description: string;
  styles?: {
    [className: string]: string;
  };
  imagesList: {
    id: number;
    comment: string;
    file: {
      id: number;
      file_name: string;
      file_path: string;
      mime_type: string;
      size: number;
    };
  }[];
  videosList: {
    id: number;
    comment: string;
    file: {
      id: number;
      file_name: string;
      file_path: string;
      mime_type: string;
      size: number;
    };
  }[];
  filesList: {
    id: number;
    comment: string;
    file: {
      id: number;
      file_name: string;
      file_path: string;
      mime_type: string;
      size: number;
    };
  }[];
  links: string[];
};

const { Title } = Typography;

type KnowledgeBaseStoreState = {
  list: { isLoaded: boolean; data: KnowledgeBase[] };
  setList: (list: { isLoaded: boolean; data: KnowledgeBase[] }) => void;
  putIds: number[];
  setPutIds: (update: (state: number[]) => number[]) => void;
  isPostInProcess: boolean;
  setIsPostInProcess: (isPostInProcess: boolean) => void;
  isSubmittable: boolean;
  setIsSubmittable: (isSubmittable: boolean) => void;
  isError: boolean;
  setIsError: (isError: boolean) => void;
  isImageUploading: boolean;
  setIsImageUploading: (isError: boolean) => void;
  isVideoUploading: boolean;
  setIsVideoUploading: (isError: boolean) => void;
  isFileUploading: boolean;
  setIsFileUploading: (isError: boolean) => void;
  filesComments: { [key: string]: string };
  setFilesComments: (
    update: (state: { [key: string]: string }) => { [key: string]: string }
  ) => void;
};

const useKnowledgeBaseStore = create<KnowledgeBaseStoreState>((set) => ({
  list: {
    isLoaded: false,
    data: [],
  },
  setList: (list) => set({ list }),
  putIds: [],
  setPutIds: (update) =>
    set((state) => ({
      putIds: update(state.putIds),
    })),
  isPostInProcess: false,
  setIsPostInProcess: (isPostInProcess) => set({ isPostInProcess }),
  isSubmittable: false,
  setIsSubmittable: (isSubmittable) => set({ isSubmittable }),
  isError: false,
  setIsError: (isError) => set({ isError }),
  isImageUploading: false,
  setIsImageUploading: (isImageUploading) => set({ isImageUploading }),
  isVideoUploading: false,
  setIsVideoUploading: (isVideoUploading) => set({ isVideoUploading }),
  isFileUploading: false,
  setIsFileUploading: (isFileUploading) => set({ isFileUploading }),
  filesComments: {},
  setFilesComments: (update) =>
    set((state) => ({
      filesComments: update(state.filesComments),
    })),
}));

const Page: React.FC<ComponentProps> = () => {
  const { "*": subRoute } = useParams();
  const navigate = useNavigate();

  const isAddMode = useMemo(() => {
    if (subRoute === "add") return true;

    return false;
  }, [subRoute]);

  const isEditMode = useMemo(() => {
    if (!subRoute?.startsWith("edit/")) return false;

    const id = parseInt(subRoute.split("/")[1], 10);

    return isNaN(id) ? false : id;
  }, [subRoute]);

  const {
    list,
    setList,
    putIds,
    setPutIds,
    isPostInProcess,
    setIsPostInProcess,
    isSubmittable,
    setIsSubmittable,
    isError,
    setIsError,
    isImageUploading,
    setIsImageUploading,
    isVideoUploading,
    setIsVideoUploading,
    isFileUploading,
    setIsFileUploading,
    filesComments,
    setFilesComments,
  } = useKnowledgeBaseStore((store) => store);

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const listRef = useRef(list);

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    listRef.current = list;
  }, [list]);

  //  Effect triggers when mode changes to edit or add. Also when some value changes. It fires validation and reset error.
  useEffect(() => {
    if (!isAddMode && !isEditMode) return;

    setIsError(false);

    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [isAddMode, isEditMode, form, values, setIsError, setIsSubmittable]);

  //  Get all knowledge base items
  const getList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/knowledge_base`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: KnowledgeBase[] = data.records.map(
            (item: KnowledgeBase) => {
              return {
                ...item,
                parent_id: item.parent_id ? item.parent_id : 0,
              };
            }
          );

          setList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (list.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setList, list]
  );

  //  When started, get all data
  useEffect(() => {
    getList();
  }, [getList]);

  //  Edit button sets edit mode or reset it
  const handleEditButton = useCallback(
    (id?: number) => {
      navigate(`/knowledge-base/edit/${id}`);
    },
    [navigate]
  );

  //  Triggers when isEditMode changed. Set initial fields value in form or reset it. Reset error.
  useEffect(() => {
    const item = listRef.current.data.find((item) => item.id === isEditMode);

    if (!item) form.resetFields();

    if (item) {
      const imagesList = (item.imagesList ? [...item.imagesList] : []).map(
        (element) => {
          return element.file
            ? {
                uid: element.file.id,
                name: element.file.file_name,
                size: element.file.size,
                type: element.file.mime_type,
                response: [
                  {
                    file_name: element.file.file_name,
                    file_path: element.file.file_path,
                    mime_type: element.file.mime_type,
                    size: element.file.size,
                    id: element.file.id,
                  },
                ],
                thumbUrl: `${apiDomain}/userFiles/${element.file.file_path}`,
              }
            : null;
        }
      );

      form.setFieldsValue({
        name: item.name,
        parent_id: item.parent_id,
        description: item.description,
        imagesList: imagesList,
        videosList: (item.videosList ? item.videosList : []).map((element) => {
          return element.file
            ? {
                uid: element.file.id,
                name: element.file.file_name,
                size: element.file.size,
                type: element.file.mime_type,
                response: [
                  {
                    file_name: element.file.file_name,
                    file_path: element.file.file_path,
                    mime_type: element.file.mime_type,
                    size: element.file.size,
                    id: element.file.id,
                  },
                ],
                thumbUrl: `${apiDomain}/userFiles/${element.file.file_path}`,
              }
            : null;
        }),
        filesList: (item.filesList ? item.filesList : []).map((element) => {
          return element.file
            ? {
                uid: element.file.id,
                name: element.file.file_name,
                size: element.file.size,
                type: element.file.mime_type,
                response: [
                  {
                    file_name: element.file.file_name,
                    file_path: element.file.file_path,
                    mime_type: element.file.mime_type,
                    size: element.file.size,
                    id: element.file.id,
                  },
                ],
                thumbUrl: `${apiDomain}/userFiles/${element.file.file_path}`,
              }
            : null;
        }),
        links: item.links.join("\n"),
      });

      setFilesComments(() => {
        const filesList = [
          ...(item.imagesList ? item.imagesList : []),
          ...(item.videosList ? item.videosList : []),
          ...(item.filesList ? item.filesList : []),
        ];

        const result: { [key: string]: string } = {};

        filesList.forEach((filesListItem) => {
          result[filesListItem.file.id.toString()] = filesListItem.comment;
        });

        return result;
      });
    }

    setIsError(false);

    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [
    isEditMode,
    form,
    list.isLoaded,
    setIsError,
    setIsSubmittable,
    setFilesComments,
  ]);

  //  Add button sets add mode or reset it
  const handleAddButton = useCallback(() => {
    navigate(`/knowledge-base/add`);
  }, [navigate]);

  const handleBackButton = useCallback(() => {
    navigate(`/knowledge-base`);
  }, [navigate]);

  const isInProcess = useMemo(() => {
    if (!!isAddMode && !!isPostInProcess) return true;

    if (!!isEditMode && !!putIds.includes(isEditMode)) return true;

    return false;
  }, [isAddMode, isPostInProcess, isEditMode, putIds]);

  //  Submit form, depends of mode
  const handleFinish = useCallback(() => {
    if (isInProcess) return;

    const fetchData = async () => {
      setIsError(false);

      const editModeId = isEditMode;

      if (editModeId)
        setPutIds((state) => {
          return [...state, editModeId];
        });

      if (isAddMode) setIsPostInProcess(true);

      const url = editModeId
        ? `${apiDomain}/api/records/knowledge_base/${editModeId}`
        : `${apiDomain}/api/records/knowledge_base`;

      const options: RequestInit = {
        method: editModeId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: values.name.trim(),
          parent_id: values.parent_id ? values.parent_id : null,
          description: values.description,
          imagesList: (values.imagesList ? values.imagesList : [])
            .map((image: any) => {
              const imageId = image?.response?.[0]?.id
                ? image.response[0].id
                : image.uid && !isNaN(Number(image.uid))
                ? Number(image.uid)
                : null;

              const comment = filesComments?.[image.uid] ?? "";

              return { id: imageId, comment: comment };
            })
            .filter((imageId: number | null) => !!imageId),
          videosList: (values.videosList ? values.videosList : [])
            .map((video: any) => {
              const videoId = video?.response?.[0]?.id
                ? video.response[0].id
                : video.uid && !isNaN(Number(video.uid))
                ? Number(video.uid)
                : null;

              const comment = filesComments?.[video.uid] ?? "";

              return { id: videoId, comment: comment };
            })
            .filter((videoId: number | null) => !!videoId),
          filesList: (values.filesList ? values.filesList : [])
            .map((file: any) => {
              const fileId = file?.response?.[0]?.id
                ? file.response[0].id
                : file.uid && !isNaN(Number(file.uid))
                ? Number(file.uid)
                : null;

              const comment = filesComments?.[file.uid] ?? "";

              return { id: fileId, comment: comment };
            })
            .filter((fileId: number | null) => !!fileId),
          links: values.links
            ? values.links.split("\n").map((link: string) => link.trim())
            : undefined,
        }),
        credentials: "include",
      };

      try {
        const response = await fetch(url, options);

        if (!response.ok) {
          const errorData = await response.json();

          const errorText: { [key in typeof errorCode]: string } = {
            nameIsEmpty: "Имя не может быть пустым",
            parentIdNotFound: "Родительский раздел не найден",
            imageNotFound:
              "Одно из загруженных изображений не найдено в базе данных",
            videoNotFound: "Одно из загруженных видео не найдено в базе данных",
            fileNotFound: "Один из загруженных файлов не найден в базе данных",
            linksIsNotAnArray: "Неправильный формат ссылок",
          };

          const errorFields: string[] = errorData.fields;
          const errorCode:
            | "nameIsEmpty"
            | "parentIdNotFound"
            | "imageNotFound"
            | "videoNotFound"
            | "fileNotFound"
            | "linksIsNotAnArray" = errorData.errorCode;

          if (errorFields.length) {
            const errorMessage = errorText[errorCode];

            errorFields.forEach((errorField) => {
              form.setFields([
                {
                  name: errorField,
                  errors: [errorMessage],
                },
              ]);
            });
          }

          throw new Error();
        }

        getList(true);

        form.resetFields();
        form
          .validateFields({ validateOnly: true })
          .then(() => setIsSubmittable(true))
          .catch(() => setIsSubmittable(false));

        navigate(`/knowledge-base`);

        if (editModeId)
          setPutIds((state) => {
            return state.filter((id) => id !== editModeId);
          });

        if (isAddMode) setIsPostInProcess(false);
      } catch (error) {
        setIsError(true);

        if (editModeId)
          setPutIds((state) => {
            return state.filter((id) => id !== editModeId);
          });

        if (isAddMode) setIsPostInProcess(false);
      }
    };

    fetchData();
  }, [
    isEditMode,
    isAddMode,
    form,
    values,
    isInProcess,
    filesComments,
    navigate,
    setIsError,
    getList,
    setIsPostInProcess,
    setPutIds,
    setIsSubmittable,
  ]);

  const handleUploadImageChange = useCallback(
    (info: UploadChangeParam) => {
      setIsImageUploading(info.file.status === "uploading");
    },
    [setIsImageUploading]
  );

  const handleUploadVideoChange = useCallback(
    (info: UploadChangeParam) => {
      setIsVideoUploading(info.file.status === "uploading");
    },
    [setIsVideoUploading]
  );

  const handleUploadFileChange = useCallback(
    (info: UploadChangeParam) => {
      setIsFileUploading(info.file.status === "uploading");
    },
    [setIsFileUploading]
  );

  const handlePreview = useCallback(async (previewFile: UploadFile) => {
    console.log(previewFile);
  }, []);

  const filterParentIds = useCallback(
    (data: KnowledgeBase[], isEditMode: number | undefined) => {
      if (isEditMode === undefined) return data;

      const getAllChildren = (parentId: number) => {
        let children = data.filter((item) => item.parent_id === parentId);
        children.forEach((child) => {
          children = children.concat(getAllChildren(child.id));
        });
        return children;
      };

      const allChildren = getAllChildren(isEditMode);

      return data.filter((item) => {
        return (
          item.id !== isEditMode &&
          !allChildren.some((child) => child.id === item.id)
        );
      });
    },
    []
  );

  //  Set columns for table
  const columns: ColumnsType = useMemo(() => {
    const result: ColumnsType = [
      {
        title: "id",
        key: "id",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as KnowledgeBase;

          return <div>{record.id}</div>;
        },
      },
      {
        title: "",
        key: "name",
        width: "90%",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as KnowledgeBase & {
            level?: number;
          };

          return (
            <div style={{ marginLeft: `${(record.level ?? 0) * 16}px` }}>
              {record.name}
            </div>
          );
        },
      },
      {
        key: "edit",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as KnowledgeBase;
          const recordStyles = record.styles;

          return (
            <>
              <Button
                onClick={() => handleEditButton(record.id)}
                className={recordStyles?.actionButton}
                disabled={isInProcess}
              >
                Изменить
              </Button>
            </>
          );
        },
      },
    ];

    return result;
  }, [isInProcess, handleEditButton]);

  const addChildrenToBranch = useCallback(
    (
      parent: KnowledgeBase & { children: KnowledgeBase[] },
      items: KnowledgeBase[]
    ) => {
      const children = items.filter((item) => item.parent_id === parent.id);

      children.forEach((child) => {
        const childWithChildren = { ...child, children: [] };

        parent.children.push(childWithChildren);

        addChildrenToBranch(childWithChildren, items);
      });
    },
    []
  );

  const flattenTree = useCallback(
    (tree: (KnowledgeBase & { children: KnowledgeBase[] })[]) => {
      return tree.reduce(
        (
          result: KnowledgeBase[],
          treeElement: KnowledgeBase & { children: KnowledgeBase[] }
        ) => {
          const { children, ...elementWithoutChildren } = treeElement;

          result.push({ ...elementWithoutChildren });

          if (children) {
            const flattenChildren = flattenTree(
              children as (KnowledgeBase & { children: KnowledgeBase[] })[]
            );

            result = result.concat(flattenChildren);
          }

          return result;
        },
        []
      );
    },
    []
  );

  const tableData = useMemo(() => {
    const getLevel = (item: { id: number; parent_id: number }): number => {
      let level = 0;

      for (let parentId = item.parent_id; parentId !== 0; ) {
        const parent = list.data.find(
          (parentItem) => parentItem.id === parentId
        );

        if (parent) {
          level++;
          parentId = parent.parent_id;

          continue;
        }

        break;
      }

      return level;
    };

    const dataWithLevel = list.data.map((item) => ({
      ...item,
      level: getLevel(item),
    }));

    const treeOfElements = dataWithLevel
      .filter((item) => item.parent_id === 0)
      .map((item) => ({
        ...item,
        children: [],
      }));

    treeOfElements.forEach((rootElement) => {
      addChildrenToBranch(rootElement, dataWithLevel);
    });

    return flattenTree(treeOfElements);
  }, [list.data, addChildrenToBranch, flattenTree]);

  return (
    <MobileInnerWrapper>
      <Row justify="space-between" align="middle" className={styles.titleBlock}>
        <Col>
          <Title level={3} className={styles.title}>
            {isAddMode
              ? `Добавление раздела`
              : isEditMode
              ? `Редактирование раздела №${isEditMode}`
              : `База знаний`}
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={
              isAddMode || isEditMode ? handleBackButton : handleAddButton
            }
            disabled={isInProcess}
          >
            {isAddMode || isEditMode ? `Вернуться к списку` : `Добавить раздел`}
          </Button>
        </Col>
      </Row>
      {(!!isAddMode || !!isEditMode) && (
        <>
          <Flex vertical gap={16}>
            <Form
              layout={"vertical"}
              form={form}
              onFinish={handleFinish}
              initialValues={{
                name: "",
                parent_id: 0,
                description: "",
              }}
            >
              <Form.Item
                name="name"
                label="Название раздела"
                rules={[
                  {
                    required: true,
                    message: "Пожалуйста, введите название раздела!",
                  },
                ]}
                validateTrigger={false}
                required={false}
              >
                <Input
                  size="large"
                  name="name"
                  disabled={isInProcess || !list.isLoaded}
                />
              </Form.Item>
              <Form.Item
                name="parent_id"
                label="Родительский раздел"
                validateTrigger={false}
                required={false}
              >
                <Select
                  placeholder="Выберите родительский раздел"
                  disabled={isInProcess || !list.isLoaded}
                >
                  <Select.Option key={0} value={0}>
                    Корневой раздел
                  </Select.Option>
                  {filterParentIds(
                    list.data,
                    isEditMode ? isEditMode : undefined
                  ).map((item) => {
                    return (
                      <Select.Option key={item.id} value={item.id}>
                        {item.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
              <Form.Item
                name="description"
                label="Описание"
                validateTrigger={false}
                required={false}
              >
                <TextArea
                  size="large"
                  name="description"
                  disabled={isInProcess || !list.isLoaded}
                />
              </Form.Item>
              <Form.Item
                label="Добавить фото"
                name="imagesList"
                valuePropName="fileList"
                getValueFromEvent={(e) => e.fileList}
              >
                <Upload
                  action={`${apiDomain}/api/upload-file`}
                  accept="image/*"
                  withCredentials={true}
                  multiple={true}
                  onChange={handleUploadImageChange}
                  onPreview={handlePreview}
                  listType="picture"
                  itemRender={(originNode, file: any) => {
                    const fileId = file.uid;
                    const comment = filesComments?.[fileId]
                      ? filesComments[fileId]
                      : "";

                    return (
                      <div>
                        {originNode}
                        <Input
                          placeholder="Комментарий"
                          value={comment}
                          onChange={(event) => {
                            setFilesComments((filesComments) => {
                              const comment = event.target.value;

                              filesComments[fileId] = comment;

                              return filesComments;
                            });
                          }}
                        />
                      </div>
                    );
                  }}
                >
                  <Button
                    icon={<UploadOutlined />}
                    disabled={isImageUploading || !list.isLoaded}
                  >
                    {isImageUploading ? `Идет загрузка` : `Загрузить файл`}
                  </Button>
                </Upload>
              </Form.Item>
              <Form.Item
                label="Добавить видео"
                name="videosList"
                valuePropName="fileList"
                getValueFromEvent={(e) => e.fileList}
              >
                <Upload
                  action={`${apiDomain}/api/upload-file`}
                  accept="video/*"
                  withCredentials={true}
                  multiple={true}
                  onChange={handleUploadVideoChange}
                  onPreview={handlePreview}
                  listType="picture"
                  itemRender={(originNode, file: any) => {
                    const fileId = file.uid;
                    const comment = filesComments?.[fileId]
                      ? filesComments[fileId]
                      : "";

                    return (
                      <div>
                        {originNode}
                        <Input
                          placeholder="Комментарий"
                          value={comment}
                          onChange={(event) => {
                            setFilesComments((filesComments) => {
                              const comment = event.target.value;

                              filesComments[fileId] = comment;

                              return filesComments;
                            });
                          }}
                        />
                      </div>
                    );
                  }}
                >
                  <Button
                    icon={<UploadOutlined />}
                    disabled={isVideoUploading || !list.isLoaded}
                  >
                    {isVideoUploading ? `Идет загрузка` : `Загрузить файл`}
                  </Button>
                </Upload>
              </Form.Item>
              <Form.Item
                label="Добавить файл"
                name="filesList"
                valuePropName="fileList"
                getValueFromEvent={(e) => e.fileList}
              >
                <Upload
                  action={`${apiDomain}/api/upload-file`}
                  withCredentials={true}
                  multiple={true}
                  onChange={handleUploadFileChange}
                  onPreview={handlePreview}
                  listType="picture"
                  itemRender={(originNode, file: any) => {
                    const fileId = file.uid;
                    const comment = filesComments?.[fileId]
                      ? filesComments[fileId]
                      : "";

                    return (
                      <div>
                        {originNode}
                        <Input
                          placeholder="Комментарий"
                          value={comment}
                          onChange={(event) => {
                            setFilesComments((filesComments) => {
                              const comment = event.target.value;

                              filesComments[fileId] = comment;

                              return filesComments;
                            });
                          }}
                        />
                      </div>
                    );
                  }}
                >
                  <Button
                    icon={<UploadOutlined />}
                    disabled={isFileUploading || !list.isLoaded}
                  >
                    {isFileUploading ? `Идет загрузка` : `Загрузить файл`}
                  </Button>
                </Upload>
              </Form.Item>
              <Form.Item
                name="links"
                label="Список ссылок (каждая ссылка с новой строки)"
                validateTrigger={false}
                required={false}
              >
                <TextArea
                  size="large"
                  name="links"
                  disabled={isInProcess || !list.isLoaded}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  size="large"
                  className={styles.button}
                  disabled={
                    !isSubmittable ||
                    !!isImageUploading ||
                    !!isVideoUploading ||
                    !!isFileUploading ||
                    !list.isLoaded
                  }
                  htmlType="submit"
                  loading={isInProcess}
                >
                  Сохранить
                </Button>
              </Form.Item>
              {!!isError && (
                <Form.Item>
                  <Text type="danger">Не удалось сохранить раздел</Text>
                </Form.Item>
              )}
            </Form>
          </Flex>
        </>
      )}
      {!isAddMode && !isEditMode && (
        <>
          <Table
            dataSource={tableData}
            columns={columns}
            rowKey="id"
            pagination={false}
            loading={!list.isLoaded}
            styles={styles}
          />
        </>
      )}
    </MobileInnerWrapper>
  );
};

export default Page;
